import { DotsVerticalIcon } from '@radix-ui/react-icons';
import { UserIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MEDIA_URL } from '@/config';
import useConfirmationStore from '@/stores/Confirmation';
import { convertUnixTimestamp } from '@/utils/unix';

import { useDeleteTeamMember } from '../../api/deleteTeamMember';
import { useTeamMembers } from '../../api/getTeamMembers';
import { useLeaveTeam } from '../../api/leaveTeam';
import { useUpdateTeamCaptain } from '../../api/updateTeamCaptain';

import { SettingsHeader } from './common-settings';

interface MembersViewProps {
  teamId: string;
  steamId: string;
  teamName: string;
  isCaptain: boolean;
}

export function MembersView({
  teamId,
  steamId,
  teamName,
  isCaptain,
}: MembersViewProps) {
  const {
    data: members,
    isLoading,
    error,
  } = useTeamMembers({
    teamId,
  });
  const deleteTeamMemberMutation = useDeleteTeamMember({
    teamId,
  });
  const assignCaptainMutation = useUpdateTeamCaptain({
    teamId,
  });
  const leaveTeamMutation = useLeaveTeam();
  const { openConfirmation } = useConfirmationStore();

  const handleDeleteMember = (memberId: string, memberName: string) => {
    openConfirmation({
      title: 'Remove Member?',
      description: `Are you sure you want to remove ${memberName} from the team?`,
      cancelLabel: 'Cancel',
      actionLabel: 'Remove',
      onAction: () => {
        deleteTeamMemberMutation.mutate(
          { teamId, memberId },
          {
            onSuccess: () => {
              toast.success(`${memberName} removed successfully!`);
            },
          },
        );
      },
      onCancel: () => {},
    });
  };

  const handleAssignCaptain = (memberId: string, memberName: string) => {
    openConfirmation({
      title: 'Assign New Captain?',
      description: `Are you sure you want to assign ${memberName} as the new captain? You will be demoted to a regular member.`,
      cancelLabel: 'Cancel',
      actionLabel: 'Assign',
      onAction: () => {
        assignCaptainMutation.mutate(
          { teamId, memberId },
          {
            onSuccess: () => {
              toast.success(`${memberName} is now the captain!`);
            },
          },
        );
      },
      onCancel: () => {},
    });
  };

  const handleLeaveTeam = () => {
    openConfirmation({
      title: 'Leave Team?',
      description: `Are you sure you want to leave ${teamName}?`,
      cancelLabel: 'Cancel',
      actionLabel: 'Leave',
      onAction: () => {
        leaveTeamMutation.mutate(
          { teamId },
          {
            onSuccess: () => {
              toast.success(`You have left ${teamName}!`);
            },
            onError: (error: any) => {
              toast.error(error.message || 'Failed to leave team');
            },
          },
        );
      },
      onCancel: () => {},
    });
  };

  if (isLoading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6">Error: {error.message}</div>;

  return (
    <>
      <SettingsHeader
        title="Members"
        description={
          isCaptain
            ? 'Manage your team members here. Assign a new captain before leaving.'
            : 'View team members and leave the team if desired.'
        }
      />
      <ScrollArea className="flex max-h-full flex-col px-6 py-4 pt-2">
        <Table>
          <TableHeader className="">
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined Date</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members && members.length > 0 ? (
              members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="size-7">
                        <AvatarImage
                          src={
                            member.user.profile_photo.includes('https')
                              ? member.user.profile_photo
                              : `${MEDIA_URL}/${member.user.profile_photo}`
                          }
                        />
                        <AvatarFallback>
                          <UserIcon />
                        </AvatarFallback>
                      </Avatar>
                      <div className="font-medium">{member.user.username}</div>
                    </div>
                  </TableCell>
                  <TableCell>{member.captain ? 'Captain' : 'Member'}</TableCell>
                  <TableCell>
                    {member.reqTimestamp
                      ? convertUnixTimestamp(member.reqTimestamp).formattedDate
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    {(isCaptain && member.user.steamid !== steamId) ||
                    (!isCaptain && member.user.steamid === steamId) ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                            size="icon"
                            disabled={
                              deleteTeamMemberMutation.isPending ||
                              assignCaptainMutation.isPending ||
                              leaveTeamMutation.isPending
                            }
                          >
                            <DotsVerticalIcon />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-32">
                          {member.user.steamid === steamId
                            ? !member.captain && (
                                <DropdownMenuItem
                                  variant="destructive"
                                  onClick={handleLeaveTeam}
                                >
                                  Leave Team
                                </DropdownMenuItem>
                              )
                            : isCaptain && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleAssignCaptain(
                                        member.id,
                                        member.user.username,
                                      )
                                    }
                                    disabled={member.captain}
                                  >
                                    Assign Captain
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    variant="destructive"
                                    onClick={() =>
                                      handleDeleteMember(
                                        member.id,
                                        member.user.username,
                                      )
                                    }
                                    disabled={member.captain}
                                  >
                                    Remove
                                  </DropdownMenuItem>
                                </>
                              )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>No members found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </>
  );
}
